/* OsPlanResultsViewRenderer.js */

"use strict";
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");
/**
 * @param {OsPlanResultsViewComponent} model
 */
class OsPlanResultsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("plan-results-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._summarySection = document.createElement("div");
        this._gridSection = document.createElement("div");
        this._emptyContainer = document.createElement("div");
        this.selectedRow = undefined;

        this._deviceErrorCountValue = undefined;
        this._deviceSuccessCountValue = undefined;

        this._planResultsGrid = undefined;
        this._registerActions();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._unregisterListeners().then(() => {
            if (this._planResultsGrid) {
                this._planResultsGrid.on(
                    "UserSorted",
                    (this._onGridUserSorted = this._onGridUserSorted.bind(this))
                );

                this._planResultsGrid.on(
                    "UserRowDoubleClicked",
                    (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
                );
            }

            if (this._executionEntity) {
                this._executionEntity.addListener(
                    "PropertiesUpdated",
                    (this._onEntityUpdated = this._onEntityUpdated.bind(this))
                );
            }

            this._model.on("UpdateGrid", (this._onUpdateGrid = this._onUpdateGrid.bind(this)));
            this._model.on("Render", (this._onRender = this._onRender.bind(this)));
            this._model.on("Loading", (this._onLoading = this._onLoading.bind(this)));
        });
    }

    _registerActionListeners() {
        this._unregisterActionListeners().then(() => {
            this._model.on(
                "ShowCancelAction",
                (this._onShowCancelAction = this._onShowCancelAction.bind(this))
            );
            this._model.on(
                "EnableDisableExecutionButtons",
                (this._onEnableDisableExecutionButtons =
                    this._onEnableDisableExecutionButtons.bind(this))
            );
        });
    }

    _registerActions() {
        this.model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel Remaining",
            name: "CancelExecution",
            handler: (this._onCancelRemaining = this._onCancelRemaining.bind(this)),
        });
        this.model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Create OS Plan from Failures",
            name: "CreateNewPlanFromFailures",
            handler: (this._onCreateNewPlan = this._onCreateNewPlan.bind(this)),
        });
        this.model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Download",
            name: "DownloadReport",
            handler: (this._onDownloadResults = this._onDownloadResults.bind(this)),
        });
        this._hideAllActions();
    }

    _unregisterActionListeners() {
        return new Promise((resolve) => {
            this._model.removeListener("ShowCancelAction", this._onShowCancelAction);
            this._model.removeListener(
                "EnableDisableExecutionButtons",
                this._onEnableDisableExecutionButtons
            );

            resolve();
        });
    }

    _unregisterListeners() {
        return new Promise((resolve) => {
            if (this._planResultsGrid) {
                this._planResultsGrid.removeListener("UserSorted", this._onGridUserSorted);
                this._planResultsGrid.removeListener(
                    "UserRowDoubleClicked",
                    this._onUserDblClickedRow
                );
            }

            if (this._executionEntity) {
                this._executionEntity.removeListener("PropertiesUpdated", this._onEntityUpdated);
            }

            this._model.removeListener("UpdateGrid", this._onUpdateGrid);
            this._model.removeListener("Render", this._onRender);
            this._model.removeListener("Loading", this._onLoading);
            resolve();
        });
    }

    _disableAllActions() {
        this._model._gameEditor.disableViewAction("CancelExecution");
        this._model._gameEditor.disableViewAction("CreateNewPlanFromFailures");
        this._model._gameEditor.disableViewAction("DownloadReport");
    }

    _enableAllActions() {
        this._model._gameEditor.enableViewAction("CancelExecution");
        this._model._gameEditor.enableViewAction("CreateNewPlanFromFailures");
        this._model._gameEditor.enableViewAction("DownloadReport");
    }

    _hideAllActions() {
        this._model._gameEditor.hideViewAction("CancelExecution");
        this._model._gameEditor.hideViewAction("CreateNewPlanFromFailures");
        this._model._gameEditor.hideViewAction("DownloadReport");
    }

    _showAllActions() {
        this._model._gameEditor.showViewAction("CancelExecution");
        this._model._gameEditor.showViewAction("CreateNewPlanFromFailures");
        this._model._gameEditor.showViewAction("DownloadReport");
    }

    _enableDisableExecutionButtons() {
        if (_.includes(["ERROR", "COMPLETE", "CANCELED"], _.get(this._model, "_plan.status"))) {
            this._model._gameEditor.disableViewAction("CancelExecution");
            this._setViewActionText("CancelExecution", "Cancel Remaining");
            this._model._gameEditor._planCancelInProgress = undefined;
            const {failuresCount} = this._getFailuresCount();
            if (failuresCount > 0) {
                this._model._gameEditor.enableViewAction("CreateNewPlanFromFailures");
            }
            this._model._gameEditor.enableViewAction("DownloadReport");
            this._showAllActions();
        }
    }

    _showCancelActionButton() {
        this._model._gameEditor.hideViewAction("CreateNewPlanFromFailures");
        this._model._gameEditor.hideViewAction("DownloadReport");
        this._model._gameEditor.showViewAction("CancelExecution");
        this._model._gameEditor.enableViewAction("CancelExecution");
        if (this._model._gameEditor._planCancelInProgress) {
            this._model._gameEditor.disableViewAction("CancelExecution");
            this._setViewActionText("CancelExecution", "Canceling..");
        }
    }

    _disableCancelActionButton() {
        this._model._gameEditor.disableViewAction("CancelExecution");
    }

    _enableCancelActionButton() {
        this._model._gameEditor.enableViewAction("CancelExecution");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._unregisterActionListeners();
        let promArr = [
            this._planResultsGrid ? this._planResultsGrid.shutdown() : Promise.resolve(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._model = null;
                this._executionEntity = null;
                this._summarySection = null;
                this._gridSection = null;
                this._emptyContainer = null;
                this.selectedRow = undefined;

                this._rootElem.remove();
                this._rootElem = null;

                this._deviceErrorCountValue = undefined;
                this._deviceSuccessCountValue = undefined;

                Promise.resolve();
            },
            (err) => {
                this._logger.warn({error: err}, "OsPlanResultsViewRenderer.shutdown() error.");
            }
        );
    }

    render() {
        this.rootElement.innerHTML = "";

        this.rootElement.appendChild(this._summarySection);
        this.rootElement.appendChild(this._gridSection);
        this.rootElement.appendChild(this._emptyContainer);
        this._clearEmpty();
        this._hideAllActions();
        this._registerActionListeners();
        this._registerListeners();

        // The veil is dropped only once the grid or empty state is painted, so neither flashes
        return this.model
            .whenModelIsReady()
            .then(() => {
                const {failuresCount} = this._getFailuresCount();

                if (this._model._executionEntity) {
                    // Drops the "Preparing execution results..." placeholder written during the wait
                    this._clearEmpty();
                    this._renderSummary(
                        this._model._gameEditor.getPlan(),
                        this._model._executionEntity
                    );

                    if (this._model._pagingExecutionResultsFeed.deviceErrorCount === 0) {
                        this.model._gameEditor.hideViewAction("CreateNewPlanFromFailures");
                    }

                    if (
                        _.includes(
                            ["ERROR", "COMPLETE", "CANCELED"],
                            _.get(this._model, "_plan.status")
                        ) &&
                        failuresCount > 0
                    ) {
                        this.model._gameEditor.enableViewAction("CreateNewPlanFromFailures");
                    } else {
                        this.model._gameEditor.disableViewAction("CreateNewPlanFromFailures");
                    }

                    this._renderGrid();
                } else {
                    this._renderEmpty();
                }
            })
            .catch((err) => {
                this._logger &&
                    this._logger.warn({error: err}, "OsPlanResultsViewRenderer.render() error.");
            })
            .then(() => this._model._hideLoader());
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _setViewActionText(buttonValue, buttonText) {
        let buttonControl = _.get(
            this._model._gameEditor._viewContainerElem.nextElementSibling.lastElementChild.querySelectorAll(
                "button-control[value='" + buttonValue + "']"
            ),
            0
        );

        if (buttonControl) {
            buttonControl.innerHTML = buttonText;
        }
    }
    _onGridUserSorted(eventData) {
        this.model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        this.selectedRow = _.get(
            this._model._pagingExecutionResultsFeed,
            "cache." + obj.selectedRow,
            false
        );
        if (this.selectedRow) {
            this._switchView(Registrations.OS_PLAN_EDITOR_SUBVIEW_RESULTS_LOGS);
        }
    }

    _onDownloadResults() {
        this.model._gameEditor.disableViewAction("DownloadReport");
        this.model._downloadWorkResults().finally(() => {
            this.model._gameEditor.enableViewAction("DownloadReport");
        });
    }

    _onCancelRemaining() {
        return this._model._gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure to Cancel Execution?",
                title: "Cancel Remaining Execution",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
            },
            () => {
                return this._model
                    ._cancelRemaining()
                    .then(() => {
                        this._disableCancelActionButton();
                        this._setViewActionText("CancelExecution", "Canceling...");
                        this._model._gameEditor._planCancelInProgress = true;
                    })
                    .catch((err) => {
                        if (err && err.userRejected) {
                            this._logger.warn(
                                {error: err},
                                "OsPlanResultsViewRenderer._onCancelRemaining()  User Canceled"
                            );
                            this._enableDisableExecutionButtons();
                            return;
                        } else {
                            this._logger.warn(
                                {error: err},
                                "OsPlanResultsViewRenderer._onCancelRemaining()  Error"
                            );
                            return this._model._modalUserFeedback.displayError(err);
                        }
                    });
            }
        );
    }

    _onCreateNewPlan() {
        this._model._createPlanFromError();
    }
    _onEntityUpdated(entityData) {
        this._renderSummary(entityData);
    }

    _onUpdateGrid() {
        // updateSummary() needs the summary DOM, which only exists once _renderSummary() has run
        if (!_.isNil(this._deviceSuccessCountValue)) {
            this.updateSummary(this._model._executionEntity);
        }
        this._renderGrid();
        this._enableDisableExecutionButtons();
    }

    _onLoading(msg) {
        this._renderEmpty(msg);
    }

    _onRender() {
        this.render();
    }

    _onShowCancelAction() {
        this._showCancelActionButton();
    }

    _onEnableDisableExecutionButtons() {
        this._enableDisableExecutionButtons();
    }
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _renderSummary(plansData, execution) {
        this._summarySection.innerHTML = "";
        this._summarySection.className = "summary-section";
        const {name, description, action} = plansData;
        const {deviceCount, deviceErrorCount, deviceSuccessCount} = execution;

        const summaryContainer = document.createElement("div");
        summaryContainer.classList.add("summary");

        // Summary column 1 : Start
        const _summaryColumn1 = document.createElement("div");
        _summaryColumn1.classList.add("summary-column");

        const _summarycol1Item1 = document.createElement("div");
        _summarycol1Item1.classList.add("summary-item");

        const _policyNameLabel = document.createElement("span");
        _policyNameLabel.classList.add("data-label");
        _policyNameLabel.innerText = "Plan Name";

        const _policyNameValue = document.createElement("span");
        _policyNameValue.classList.add("data-value");
        _policyNameValue.title = name;
        _policyNameValue.innerText = name;

        _summarycol1Item1.appendChild(_policyNameLabel);
        _summarycol1Item1.appendChild(_policyNameValue);

        const _summarycol1Item2 = document.createElement("div");
        _summarycol1Item2.classList.add("summary-item", "description-section");

        const _descriptionLabel = document.createElement("span");
        _descriptionLabel.classList.add("data-label");
        _descriptionLabel.innerText = "Description";

        const _descriptionValue = document.createElement("div");
        _descriptionValue.classList.add("data-value", "description-content");
        _descriptionValue.title = description;
        _descriptionValue.innerText = description;

        _summarycol1Item2.appendChild(_descriptionLabel);
        _summarycol1Item2.appendChild(_descriptionValue);

        _summaryColumn1.appendChild(_summarycol1Item1);
        _summaryColumn1.appendChild(_summarycol1Item2);

        // Summary column 1 : End

        // Summary column 2 : Start

        const _summaryColumn2 = document.createElement("div");
        _summaryColumn2.classList.add("summary-column");

        const _summarycol2Item1 = document.createElement("div");
        _summarycol2Item1.classList.add("summary-item");

        const _actionLabel = document.createElement("span");
        _actionLabel.classList.add("data-label");
        _actionLabel.innerText = "Action";

        const _actionValue = document.createElement("div");
        _actionValue.classList.add("data-value");
        _actionValue.title = action;
        _actionValue.innerText = action;

        _summarycol2Item1.appendChild(_actionLabel);
        _summarycol2Item1.appendChild(_actionValue);

        _summaryColumn2.appendChild(_summarycol2Item1);

        // Summary column 2 : End

        // Summary column 3 : Start

        const _summaryColumn3 = document.createElement("div");
        _summaryColumn3.classList.add("summary-column");

        const _summarycol3Item1 = document.createElement("div");
        _summarycol3Item1.classList.add("summary-item");

        const _deviceCountValue = document.createElement("span");
        _deviceCountValue.classList.add("data-value");
        _deviceCountValue.title = deviceCount;
        _deviceCountValue.innerText = deviceCount;

        const _deviceCountLabel = document.createElement("span");
        _deviceCountLabel.classList.add("data-label");
        _deviceCountLabel.innerText = "Total Devices";

        _summarycol3Item1.appendChild(_deviceCountValue);
        _summarycol3Item1.appendChild(_deviceCountLabel);

        const _summarycol3Item2 = document.createElement("div");
        _summarycol3Item2.classList.add("summary-item");

        this._deviceErrorCountValue = document.createElement("span");
        this._deviceErrorCountValue.classList.add("data-value");
        this._deviceErrorCountValue.title = deviceErrorCount;
        this._deviceErrorCountValue.innerText = deviceErrorCount;

        const _deviceErrorCountLabel = document.createElement("span");
        _deviceErrorCountLabel.classList.add("data-label");
        _deviceErrorCountLabel.innerText = "Device Failures";

        _summarycol3Item2.appendChild(this._deviceErrorCountValue);
        _summarycol3Item2.appendChild(_deviceErrorCountLabel);

        const _summarycol3Item3 = document.createElement("div");
        _summarycol3Item3.classList.add("summary-item");

        this._deviceSuccessCountValue = document.createElement("span");
        this._deviceSuccessCountValue.classList.add("data-value");
        this._deviceSuccessCountValue.title = deviceSuccessCount;
        this._deviceSuccessCountValue.innerText = deviceSuccessCount;

        const _deviceSuccessCountLabel = document.createElement("span");
        _deviceSuccessCountLabel.classList.add("data-label");
        _deviceSuccessCountLabel.innerText = "Device Success";

        _summarycol3Item3.appendChild(this._deviceSuccessCountValue);
        _summarycol3Item3.appendChild(_deviceSuccessCountLabel);

        _summaryColumn3.appendChild(_summarycol3Item1);
        _summaryColumn3.appendChild(_summarycol3Item2);
        _summaryColumn3.appendChild(_summarycol3Item3);

        // Summary column 3 : End

        // Summary container append : Start
        summaryContainer.appendChild(_summaryColumn1);
        summaryContainer.appendChild(_summaryColumn2);
        summaryContainer.appendChild(_summaryColumn3);
        // Summary container append : End

        this._summarySection.appendChild(summaryContainer);
    }

    updateSummary(executionEntity) {
        const {deviceErrorCount, deviceSuccessCount} = executionEntity;

        this._deviceSuccessCountValue.innerText = deviceSuccessCount;
        this._deviceErrorCountValue.innerText = deviceErrorCount;
    }

    _renderGrid() {
        this._gridSection.classList.add("grid-section");
        this._gridSection.innerHTML = "";
        this._planResultsGrid = new EntityGrid({
            gridKey: this._model._gridKey,
            columns: this._model._columnConfig,
            pagingFeed: this._model._pagingExecutionResultsFeed,
            showHeader: true,
        });

        this._gridSection.appendChild(this._planResultsGrid.renderer.rootElement);

        this._registerListeners();
    }

    _renderEmpty(msg = "No results to display") {
        this._emptyContainer.classList.add("empty-container");
        this._emptyContainer.innerHTML = msg;
    }

    // _emptyContainer is reused across renders, so any previous message must be cleared
    _clearEmpty() {
        this._emptyContainer.classList.remove("empty-container");
        this._emptyContainer.innerHTML = "";
    }

    _getFailuresCount() {
        const devicesCache = _.get(this._model, "_pagingExecutionResultsFeed.cache", []);

        let failuresCount = 0;
        _.forOwn(devicesCache, (element) => {
            if (
                element.status === "FAILED" ||
                element.status === "ERROR" ||
                element.status === "CANCELED" ||
                element.status === "PENDING"
            ) {
                failuresCount = failuresCount + 1;
            }
        });

        return {devicesCache, failuresCount};
    }

    /** navigate inside view tab */
    _switchView(switchTo, params) {
        this._model._gameEditor.setActiveView(
            switchTo,
            [
                {
                    value: {
                        fqn: Registrations.OS_PLAN_EDITOR_VIEW_RESULTS,
                        viewState: {
                            configExecutionEntity: this.selectedRow,
                        },
                    },
                    content: "Details",
                    left: {
                        arrow: {
                            direction: "left",
                        },
                    },
                },
            ],
            params
                ? params
                : {
                      configExecutionEntity: this.selectedRow,
                  }
        );
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = OsPlanResultsViewRenderer;
